<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import RichTextEditor from '../../components/RichTextEditor.svelte';
    import { useForm } from '@inertiajs/svelte';

    let { settings, timezones } = $props();

    const form = useForm({
        cutoff_time: settings.cutoff_time.substring(0, 5),
        timezone: settings.timezone,
        feedback_recipients: settings.feedback_recipients,
        leaderboard_enabled: settings.leaderboard_enabled === '1',
        case_submission_enabled: settings.case_submission_enabled === '1',
        welcome_message: settings.welcome_message ?? '',
        reset_date: settings.reset_date ?? '',
    });

    function submit(e: Event) {
        e.preventDefault();
        $form.patch('/admin/settings');
    }
</script>

<svelte:head>
    <title>Settings — Admin — Case of the Day</title>
</svelte:head>

<h1 class="text-2xl font-bold text-gray-100 mb-6">Settings</h1>

    <form onsubmit={submit}>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Left column -->
            <div class="space-y-6">
                <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
                    <h2 class="text-lg font-semibold text-gray-200">Case Timing</h2>

                    <div>
                        <label for="timezone" class="block text-sm font-medium text-gray-300 mb-1">Timezone</label>
                        <select
                            id="timezone"
                            bind:value={$form.timezone}
                            class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                        >
                            {#each timezones as tz (tz)}
                                <option value={tz}>{tz.replaceAll('_', ' ')}</option>
                            {/each}
                        </select>
                        <p class="text-xs text-gray-500 mt-1">The timezone used for case scheduling and cutoff times.</p>
                        {#if $form.errors.timezone}<p class="text-red-500 text-sm mt-1">{$form.errors.timezone}</p>{/if}
                    </div>

                    <div>
                        <label for="cutoff_time" class="block text-sm font-medium text-gray-300 mb-1">Daily Cutoff Time</label>
                        <input
                            id="cutoff_time"
                            type="time"
                            bind:value={$form.cutoff_time}
                            class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                        />
                        <p class="text-xs text-gray-500 mt-1">Cases become "revealed" after this time each day. Answers are locked in before this cutoff.</p>
                        {#if $form.errors.cutoff_time}<p class="text-red-500 text-sm mt-1">{$form.errors.cutoff_time}</p>{/if}
                    </div>

                    <div>
                        <label for="reset_date" class="block text-sm font-medium text-gray-300 mb-1">Reset Date</label>
                        <div class="flex items-center gap-2">
                            <input
                                id="reset_date"
                                type="date"
                                bind:value={$form.reset_date}
                                class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                            />
                            {#if $form.reset_date}
                                <button
                                    type="button"
                                    onclick={() => $form.reset_date = ''}
                                    class="text-sm text-gray-400 hover:text-gray-200 shrink-0"
                                >
                                    Clear
                                </button>
                            {/if}
                        </div>
                        <p class="text-xs text-gray-500 mt-1">When set, scheduled cases and answers from before this date are hidden on the public site. Admins can still see everything. Useful for starting a clean slate each year.</p>
                        {#if $form.errors.reset_date}<p class="text-red-500 text-sm mt-1">{$form.errors.reset_date}</p>{/if}
                    </div>
                </div>

                <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
                    <h2 class="text-lg font-semibold text-gray-200">Feedback</h2>

                    <div>
                        <label for="feedback_recipients" class="block text-sm font-medium text-gray-300 mb-1">Feedback Recipients</label>
                        <input
                            id="feedback_recipients"
                            type="text"
                            bind:value={$form.feedback_recipients}
                            class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                        />
                        <p class="text-xs text-gray-500 mt-1">Comma-separated email addresses that receive feedback form submissions.</p>
                        {#if $form.errors.feedback_recipients}<p class="text-red-500 text-sm mt-1">{$form.errors.feedback_recipients}</p>{/if}
                    </div>
                </div>

                <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
                    <h2 class="text-lg font-semibold text-gray-200">Features</h2>

                    <label for="case_submission_enabled" class="flex items-center gap-3 cursor-pointer">
                        <input
                            id="case_submission_enabled"
                            type="checkbox"
                            bind:checked={$form.case_submission_enabled}
                            class="h-4 w-4 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500"
                        />
                        <div>
                            <span class="text-sm font-medium text-gray-300">Case submission</span>
                            <p class="text-xs text-gray-500">Allow users to create and submit cases. Hides the "My Cases" nav item when disabled.</p>
                        </div>
                    </label>
                    {#if $form.errors.case_submission_enabled}<p class="text-red-500 text-sm mt-1">{$form.errors.case_submission_enabled}</p>{/if}

                    <label for="leaderboard_enabled" class="flex items-center gap-3 cursor-pointer">
                        <input
                            id="leaderboard_enabled"
                            type="checkbox"
                            bind:checked={$form.leaderboard_enabled}
                            class="h-4 w-4 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500"
                        />
                        <div>
                            <span class="text-sm font-medium text-gray-300">Leaderboard</span>
                            <p class="text-xs text-gray-500">Show the leaderboard on the home page.</p>
                        </div>
                    </label>
                    {#if $form.errors.leaderboard_enabled}<p class="text-red-500 text-sm mt-1">{$form.errors.leaderboard_enabled}</p>{/if}
                </div>
            </div>

            <!-- Right column -->
            <div>
                <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
                    <h2 class="text-lg font-semibold text-gray-200">Welcome Message</h2>

                    <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">Dashboard Welcome Message</label>
                        <RichTextEditor
                            value={$form.welcome_message}
                            placeholder="Enter a welcome message for the dashboard..."
                            extended
                            onchange={(html) => { $form.welcome_message = html; }}
                        />
                        <p class="text-xs text-gray-500 mt-1">Shown at the top of the main dashboard page. Leave empty to hide.</p>
                        {#if $form.errors.welcome_message}<p class="text-red-500 text-sm mt-1">{$form.errors.welcome_message}</p>{/if}
                    </div>
                </div>
            </div>
        </div>

        <div class="flex items-center gap-3 mt-6">
            <button
                type="submit"
                disabled={$form.processing}
                class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors"
            >
                {$form.processing ? 'Saving...' : 'Save Settings'}
            </button>
            {#if $form.recentlySuccessful}
                <span class="text-sm text-green-400">Saved.</span>
            {/if}
        </div>
    </form>
